"use client";

import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { CONTACT_SUBJECT_KEYS, type ContactSubjectKey } from "@/lib/contact-subjects";

export default function ContactForm({
  defaultSubjectKey = "",
}: {
  defaultSubjectKey?: ContactSubjectKey | "";
}) {
  const t = useTranslations("ContactPage");
  const initialSubject =
    defaultSubjectKey && (CONTACT_SUBJECT_KEYS as readonly string[]).includes(defaultSubjectKey)
      ? defaultSubjectKey
      : "";

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    subjectKey: initialSubject as ContactSubjectKey | "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      setError(t("validationNameMessage"));
      return;
    }
    setLoading(true);
    setError("");
    const subjectLabel =
      form.subjectKey && (CONTACT_SUBJECT_KEYS as readonly string[]).includes(form.subjectKey)
        ? t(`subjects.${form.subjectKey}` as "subjects.general")
        : "";
    const res = await fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: form.name,
        phone: form.phone,
        email: form.email,
        subject: subjectLabel,
        message: form.message,
      }),
    });
    setLoading(false);
    if (res.ok) {
      setSent(true);
    } else {
      let msg = t("errorSend");
      try {
        const d = await res.json();
        if (d.error) msg = d.error;
      } catch {
        /* keep generic */
      }
      setError(msg);
    }
  }

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
          <CheckCircle2 size={32} className="text-emerald-500" />
        </div>
        <div>
          <p className="font-bold text-gray-900 text-lg">{t("successTitle")}</p>
          <p className="text-gray-500 text-sm mt-1">{t("successHint")}</p>
        </div>
        <button
          onClick={() => {
            setSent(false);
            setForm({
              name: "",
              phone: "",
              email: "",
              subjectKey: "",
              message: "",
            });
          }}
          className="text-sm text-[#E63946] hover:underline"
        >
          {t("sendAnother")}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">{t("nameLabel")}</label>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder={t("namePlaceholder")}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946]/20 transition-all"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">{t("phoneLabel")}</label>
          <input
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder={t("phonePlaceholder")}
            type="tel"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946]/20 transition-all"
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">{t("emailLabel")}</label>
        <input
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
          placeholder={t("emailPlaceholder")}
          type="email"
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946]/20 transition-all"
        />
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1.5">{t("topicLabel")}</label>
        <div className="flex flex-wrap gap-2">
          {CONTACT_SUBJECT_KEYS.map((key) => {
            const label = t(`subjects.${key}`);
            return (
              <button
                key={key}
                type="button"
                onClick={() =>
                  set(
                    "subjectKey",
                    form.subjectKey === key ? "" : key
                  )
                }
                className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                  form.subjectKey === key
                    ? "bg-[#E63946] text-white border-[#E63946]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 block mb-1">{t("messageLabel")}</label>
        <textarea
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          placeholder={t("messagePlaceholder")}
          rows={5}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946]/20 transition-all resize-none"
        />
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-xl">{error}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#c1121f] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors shadow-sm"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <Send size={15} />
            {t("submit")}
          </>
        )}
      </button>
    </form>
  );
}
